/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { useEffect, useState, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Card, Checkbox, Col, Divider, Empty, Form, Input, Row, Select, Spin, Tooltip, Typography } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import FormCard from './FormCard';
import Prompt from '../UI/Prompt';
import { PlusIcon } from '../Icons/PlusIcon';
import { DeleteIcon } from '../Icons/DeleteIcon';

// Misc imports
import useUserPermissions from '../../utils/hooks/useUserPermissions';

import { DATA_RETENTION_OPTIONS, PERMISSIONS_GROUP_ADMIN } from '../../constants/main';

import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useUpdateBotMutation } from '../../store/bots/botsApi';

import useTheme from '../../utils/hooks/useTheme';
import is from '../../utils/is';

const anonymizationDataType = [
  { title: 'PERSON', description: 'People, including fictional.' },
  { title: 'GPE', description: 'Countries, cities, states.' },
  { title: 'NORP', description: 'Nationalities or religious or political groups.' },
  { title: 'FAC', description: 'Buildings, airports, highways, bridges, etc.' },
  { title: 'ORG', description: 'Companies, agencies, institutions, etc.' },
  { title: 'LOC', description: 'Non-GPE locations, mountain ranges, bodies of water.' },
  { title: 'PRODUCT', description: 'Objects, vehicles, foods, etc. (Not services.)' },
  { title: 'EVENT', description: 'Named hurricanes, battles, wars, sports events, etc.' },
  { title: 'WORK_OF_ART', description: 'Titles of books, songs, etc.' },
  { title: 'LAW', description: 'Named documents made into laws.' },
  { title: 'LANGUAGE', description: 'Any named language.' },
  { title: 'DATE', description: 'Absolute or relative dates or periods.' },
  { title: 'TIME', description: 'Times smaller than a day.' },
  { title: 'PERCENT', description: 'Percentage, including ”%“.' },
  { title: 'MONEY', description: 'Monetary values, including unit.' },
  { title: 'QUANTITY', description: 'Measurements, as of weight or distance.' },
  { title: 'ORDINAL', description: '“first”, “second”, etc.' },
  { title: 'CARDINAL', description: 'Numerals that do not fall under another type.' }];

const InputsLayoutCols = { xs: 24, md: 18, xl: 14, xxl: 12 };

const PrivacySettingsForm = () => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { formProps } = useTheme();
  const { tenantId, orgId } = useParams();
  const userPermissions = useUserPermissions(orgId, tenantId);
  const canEdit = userPermissions.some(permission => PERMISSIONS_GROUP_ADMIN.includes(permission));

  const { data: bot, isFetching: isFetchingBot, isError: fetchBotError } = getCurrentQueryState('fetchBot', { orgId, tenantId });
  const [updateBot, { isLoading: isUpdatingBot }] = useUpdateBotMutation({ fixedCacheKey: `updateTenantSettings` });

  const [isFieldsTouched, setIsFieldsTouched] = useState(false);
  const [, updateState] = useState();

  const [transformedInitialValues, setTransformedInitialValues] = useState({});

  useEffect(()=>{
    transformInitialValues(bot);
  }, [bot]);

  // Transform the bot data before setting as initial values
  const transformInitialValues = (botData) => {
    if (!botData) {
      return botData;
    }

    const headers = botData?.settings?.privacy?.anonymizationSettings?.serviceConfiguration?.headers;

    const isServiceConfigurationEnabled = !!botData.settings.privacy?.anonymizationSettings?.serviceConfiguration?.url || false;

    let namedEntitiesConcatenated = [...(botData?.settings?.privacy?.anonymizationSettings?.namedEntities ?? [])];
    if (botData?.settings?.privacy?.anonymizationSettings?.regexEntities?.length) {
      const regexEntities = botData?.settings?.privacy?.anonymizationSettings?.regexEntities.map(entity => ({
        name: entity.name,
        type: null,
        pattern: entity.pattern,
        replacement: entity.replacement
      }));
      namedEntitiesConcatenated = [...namedEntitiesConcatenated, ...regexEntities];
    }

    if (headers && typeof headers === 'object') {
      // Transform from { key1: value1, key2: value2 } to [{ headerName: key1, headerValue: value1 }, ...]
      const transformedHeaders = Object.entries(headers).map(([key, value]) => ({
        headerName: key,
        headerValue: value
      }));

      // Update settings.privacy.anonymizationSettings
      const anonymizationSettings = {
        ...botData.settings?.privacy?.anonymizationSettings,
        namedEntities: [...namedEntitiesConcatenated],
        regexEntities: [],
        serviceConfiguration: {
          ...botData.settings?.privacy?.anonymizationSettings?.serviceConfiguration,
          headers: transformedHeaders
        }
      };
      const transformedInitialValues = {
        ...botData,
        serviceConfigurationEnabled: isServiceConfigurationEnabled,
        settings: {
          ...botData.settings,
          privacy: {
            ...botData.settings.privacy,
            anonymizationSettings
          }
        }
      };

      setTransformedInitialValues(transformedInitialValues);
      return;
    }

    const transformedInitialValues = {
      ...botData,
      settings: {
        ...botData.settings,
        privacy: {
          ...botData.settings.privacy,
          anonymizationSettings: {
            ...botData.settings?.privacy?.anonymizationSettings,
            namedEntities: [...namedEntitiesConcatenated],
            regexEntities: []
          }
        }
      },
      serviceConfigurationEnabled: isServiceConfigurationEnabled
    };
    setTransformedInitialValues(transformedInitialValues);
    return;
  };

  // Handle form submission - transform form values to send to the backend
  const onSubmit = async (values) => {
    const namedEntities = values.settings.privacy.anonymizationSettings?.namedEntities?.filter(entity => !entity.pattern) || [];
    const regexEntities = values.settings.privacy.anonymizationSettings?.namedEntities?.filter(entity => entity.pattern) || [];

    // If service configuration settings is disabled (checkbox) then remove headers and url
    if (!values.serviceConfigurationEnabled) {
      if (values.settings.privacy.anonymizationSettings?.serviceConfiguration?.headers) {
        delete values.settings.privacy.anonymizationSettings.serviceConfiguration.headers;
      }
      if (values.settings.privacy.anonymizationSettings.serviceConfiguration?.url) {
        values.settings.privacy.anonymizationSettings.serviceConfiguration.url = '';
      }
    }

    // Transform headers (if any) to match backend structure
    const _headers = values.settings.privacy.anonymizationSettings?.serviceConfiguration?.headers;
    if(_headers && _headers.length === 0) {
      delete values.settings.privacy.anonymizationSettings.serviceConfiguration.headers;
    }

    if (_headers && _headers.length) {
      const transformedHeaders = values.settings.privacy.anonymizationSettings.serviceConfiguration.headers?.reduce((acc, item) => {
        acc[item.headerName] = item.headerValue;
        return acc;
      }, {});
      values.settings.privacy.anonymizationSettings.serviceConfiguration.headers = { ...transformedHeaders };
    }

    const valuesToSubmit = {
      ...values,
      settings: {
        ...values?.settings,
        privacy: {
          ...values?.settings.privacy,
          anonymizationSettings: {
            ...values?.settings.privacy.anonymizationSettings,
            namedEntities: namedEntities,
            regexEntities: regexEntities
          }
        }
      }
    };

    const isSaveSuccessful = await updateBot({ orgId, tenantId, data: valuesToSubmit }).unwrap();
    if (isSaveSuccessful) {
      setIsFieldsTouched(false);
    }

    return;
  };

  // Method with JSX with Form Inputs to add Header Entity
  const renderServiceConfigurationHeadersEntities = (entity, entities) => (
    <Row gutter={24}>
      <Col xs={24} md={12}>
        <Form.Item
          name={[entity.name, 'headerName']}
          label={t('Header Name')}
          disabled={!canEdit}
          className="mb-1"
          labelCol={{ span: 24 }}    // Make label take full width
          wrapperCol={{ span: 24 }}  // Make control take full width
        >
          <Input placeholder={t('Enter header name')} />
        </Form.Item>
      </Col>
      <Col xs={24} md={12}>
        <Form.Item
          name={[entity.name, 'headerValue']}
          label={t('Header Value')}
          className="mb-1"
          disabled={!canEdit}
          labelCol={{ span: 24 }}    // Make label take full width
          wrapperCol={{ span: 24 }}  // Make control take full width
        >
          <Input placeholder={t('Enter header value')}/>
        </Form.Item>
      </Col>
    </Row>
  );

  // Method with JSX with Form Inputs to add Anonymization Entity Choices
  const renderAnonymizationEntityChoices = (entity, entities, index) => {
    const isRegex = is.existy(form.getFieldValue(['settings', 'privacy', 'anonymizationSettings', 'namedEntities', entity.name, 'pattern']));
    return (
      <Fragment>
        <Row gutter={24}>  {/* Add gutter to match Form spacing */}
          <Col xs={24}>
            <Form.Item
              className="mb-2"
              name={[entity.name, 'type']}
              label={t('Data Type')}
              labelCol={{ span: 24 }}    // Make label take full width
              wrapperCol={{ span: 24 }}  // Make control take full width
            >
              <Select
                placeholder={!isRegex ? t('Choose Data Type') : t('Custom Regex')}
                size="large"
                disabled={!canEdit || isRegex}
                getPopupContainer={triggerNode => triggerNode.parentNode}
                showSearch
                filterOption={(input, option) =>
                  (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                  || (option?.description ?? '').toLowerCase().includes(input.toLowerCase())
                }
                dropdownRender={(menu) => (
                  <>
                    <Row align="middle" justify="space-between" wrap="nowrap" className="w-100 py-2 px-3">
                      <Col>
                        <Typography.Text strong type="secondary">{t('Select Built-in Data Type')}</Typography.Text>
                      </Col>
                      <Col>

                        <Typography.Link
                          onClick={(e) => {
                            e.preventDefault();
                            form.setFieldValue(['settings', 'privacy', 'anonymizationSettings', 'namedEntities', entity.name, 'pattern'], '');
                          }}
                          strong
                        >
                          {t('Add a custom Regex')}
                        </Typography.Link>
                      </Col>
                    </Row>
                    {menu}
                  </>
                )}
                options={anonymizationDataType.map((item) => (
                  {
                    label: <>
                      {t(item.title)}
                      <span className="text-secondary fs-sm">&nbsp;-&nbsp;{t(item.description)}</span>
                    </>,
                    value: item.title,
                    description: item.description
                  }
                ))}
              />
            </Form.Item>
            {
              isRegex
                ? <Form.Item
                  label={<Typography.Text>
                    {t('Regex pattern')}

                    <Tooltip
                      className="ml-1"
                      placement="top"
                      title={t('Do not add leading and trailing slashes')}
                    >
                      <QuestionCircleOutlined className="fs-sm position-absolute" />
                    </Tooltip>
                  </Typography.Text>}
                  name={[entity.name, 'pattern']}
                  wrapperCol={{ span: 24 }}  // Make control take full width
                  rules={[{
                    required: true,
                    message: t('Regex pattern is required')
                  }, {
                    validator: async (rule, value) => {
                      const isValid = is.validRegex(value);
                      if (!isValid) {
                        throw new Error(rule.message);
                      }
                    },
                    message: t('Please enter a valid Regex pattern')
                  }]}
                  normalize={(value) => {
                    // Remove surrounding forward slashes and unescape backslashes
                    if (value) {
                      return value.replace(/^\/|\/$/g, '');
                    }
                    return value;
                  }}
                >
                  <Input
                    placeholder={t('Enter regex pattern')}
                    size="large"
                    prefix={<span className="text-muted">/</span>}
                  />
                </Form.Item>
                : null
            }
          </Col>
        </Row>
        <Row gutter={24}>  {/* Keep consistent gutter */}
          <Col xs={24} md={12}>
            <Form.Item
              name={[entity.name, 'name']}
              label={t('Name')}
              className="mb-1"
              disabled={!canEdit}
              labelCol={{ span: 24 }}    // Make label take full width
              wrapperCol={{ span: 24 }}  // Make control take full width
            >
              <Input size="large" placeholder={t('Enter name for data type')} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name={[entity.name, 'replacement']}
              label={t('Replacement Text')}
              className="mb-1"
              disabled={!canEdit}
              labelCol={{ span: 24 }}    // Make label take full width
              wrapperCol={{ span: 24 }}  // Make control take full width
            >
              <Input size="large" placeholder={t('Enter replacement text')}/>
            </Form.Item>
          </Col>
        </Row>
      </Fragment>
    );
  };

  const renderContent = () => {
    if (!bot && isFetchingBot) {
      return (
        <Card>
          <Row justify="center">
            <Col span={24}>
              <Spin className="d-block" size="large" spinning />
            </Col>
          </Row>
        </Card>
      );
    }

    if (fetchBotError) {
      return (
        <Empty
          description={
            <Row gutter={16} className="mt-3">
              <Col md={{ span: 12, offset: 6 }}>
                {t('There was an error fetching your agent, please refresh your page. If the problem persists, please contact an administrator.')}
              </Col>
            </Row>
          }
          className="mt-3 mb-6"
        />
      );
    }

    if ((!bot || !bot?.settings) && !isFetchingBot) {
      return (
        <Empty
          description={
            <Row gutter={16} className="mt-3">
              <Col md={{ span: 12, offset: 6 }}>
                {t('There are no Agent Settings available. Please contact an administrator.')}
              </Col>
            </Row>
          }
          className="mt-3 mb-6"
        />
      );
    }

    if (bot && Object.keys(transformedInitialValues).length) {
      return (
        <FormCard
          name="privacySettingsForm"
          layout="vertical"
          labelCol={{ xs: 24, md: 10, xl: 8, xxl: 6 }}
          wrapperCol={{ xs: 24, md: 18, xl: 14, xxl: 12 }}
          onFinish={(values) => {
            onSubmit(values);
          }}
          form={form}
          initialValues={transformedInitialValues}
          onFieldsChange={(_, allFields) => {
            setIsFieldsTouched(true);
            updateState(allFields);
          }}
          footerActions={[
            canEdit && (
              <Button
                key="submit"
                type="primary"
                size="large"
                disabled={isFetchingBot || isUpdatingBot}
                onClick={() => form.submit()}
              >
                {t('Save Changes')}
              </Button>
            )
          ]}
        >
          <Row gutter={24}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('Chat Privacy Settings')}
              </Typography.Text>
              <Typography.Paragraph className={`${formProps.descriptionMobileDistance} fs-sm text-muted`}>
                {t('Configure the chat privacy settings of your agent')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              <Form.Item
                label={t('Data Retention')}
                name={'dataRetentionPeriod'}
                className="mb-2"
                disabled={!canEdit}
                labelCol={{ ...InputsLayoutCols }}
                wrapperCol={{ ...InputsLayoutCols }}
              >
                <Select
                  size="large"
                  className="w-100"
                  disabled={!canEdit}
                  placeholder={t('Please input a data retention time in months.')}
                  options={DATA_RETENTION_OPTIONS.map((period) => ({ label: period.label, value: period.value }))}
                  getPopupContainer={triggerNode => triggerNode.parentElement}
                />
              </Form.Item>

              <Form.Item
                name={['settings', 'privacy', 'censorUserInput']}
                valuePropName="checked"
                className="mb-2"
              >
                <Checkbox disabled={!canEdit}>{t('Obfuscate User Input')}</Checkbox>
              </Form.Item>
            </Col>
          </Row>

          <Divider className="mt-2" />

          <Row gutter={24}>
            <Col xs={formProps.mainColSpan}>
              <Typography.Text className="fs-base">
                {t('Anonymization Settings ')}
              </Typography.Text>
              <Typography.Paragraph className={`${formProps.descriptionMobileDistance} fs-sm text-muted`}>
                {t('Configure how sensitive user data, such as names, email addresses, or other personal information, is detected and anonymized in conversations to ensure privacy and compliance with data protection regulations.')}
              </Typography.Paragraph>
            </Col>

            <Col xs={formProps.secondaryColSpan}>
              {/* Named entities list */}
              <Form.Item wrapperCol={24} shouldUpdate>
                {() => (
                  <Form.List
                    name={['settings', 'privacy', 'anonymizationSettings', 'namedEntities']}
                  >
                    {(anonymizationEntities, { add: addEntity, remove: removeEntity }, { errors: responsesErrors }) => (
                      <Fragment>
                        {anonymizationEntities.map((entity, index) => {
                          return (
                            <Row key={entity.key} wrap="nowrap" align="middle">
                              <Col xs={23} md={17} xl={14} xxl={12}>
                                <div className="bg-layout p-3 mb-3 border-radius-base">
                                  {renderAnonymizationEntityChoices(entity, anonymizationEntities, index)}
                                </div>
                              </Col>

                              {/* Remove anonymization entity button */}
                              <Col flex="none">
                                <Button
                                  type="text"
                                  shape="circle"
                                  className="text-primary"
                                  icon={<DeleteIcon />}
                                  onClick={() => removeEntity(entity.name)}
                                />
                              </Col>
                              {/* Remove anonymization entity button */}
                            </Row>
                          );
                        })}

                        <Form.ErrorList errors={responsesErrors} />

                        <Row>
                          <Col>
                            {/* Add anonymization entity button */}
                            <Button
                              type="dashed"
                              size="large"
                              block
                              onClick={() => {
                                addEntity();
                              }}
                              className="text-secondary mb-5"
                              icon={<PlusIcon />}
                              disabled={!canEdit}
                            >
                              {t('Add Data Type')}
                            </Button>
                            {/* Add anonymization entity button */}
                          </Col>
                        </Row>
                      </Fragment>
                    )}
                  </Form.List>
                )}
              </Form.Item>
              {/* ./ Named entities list */}

              {/* Default Replacement text */}
              <Form.Item
                name={['settings', 'privacy', 'anonymizationSettings', 'defaultReplacement']}
                label={t('Default Replacement')}
                labelCol={{ ...InputsLayoutCols }}    // Make label take full width
                wrapperCol={{ ...InputsLayoutCols }}  // Make control take full width
              >
                <Input placeholder={t('Enter replacement text')} size="large" />
              </Form.Item>
              {/* ./ Default Replacement text*/}

              {/* Enable Service Configuration */}
              <Form.Item
                name="serviceConfigurationEnabled"
                valuePropName="checked"
                className="mb-2"
              >
                <Checkbox disabled={!canEdit} >
                  {t('Service Configuration')}
                </Checkbox>
              </Form.Item>
              {/* ./ Enable Service Configuration */}

              {/* Service Configuration */}
              <Form.Item
                shouldUpdate={(prevValues, currentValues) => prevValues?.serviceConfigurationEnabled !== currentValues?.serviceConfigurationEnabled}
                wrapperCol={24}
              >
                {({ getFieldValue }) => {
                  const enabled = getFieldValue(['serviceConfigurationEnabled']);
                  return (
                    enabled ? (
                      <Fragment>
                        {/* Service Configuration URL */}
                        <Form.Item
                          label={t('URL')}
                          name={['settings', 'privacy', 'anonymizationSettings', 'serviceConfiguration', 'url']}
                          wrapperCol={{ xs: 24, md: 18, xl: 14, xxl: 12 }}
                        >
                          <Input placeholder={t('Enter url')}/>
                        </Form.Item>
                        {/* ./ Service Configuration URL */}
                        {/* Headers */}
                        <Form.List
                          name={['settings', 'privacy', 'anonymizationSettings', 'serviceConfiguration', 'headers']}
                        >
                          {(serviceConfigurationHeaders, { add: addEntity, remove: removeEntity }, { errors: responsesErrors }) => {
                            return (
                              <Fragment>
                                {serviceConfigurationHeaders.map((entity, index) => {
                                  return (
                                    <Row key={entity.key} wrap={false} align="middle">
                                      <Col xs={23} md={17} xl={14} xxl={12}>
                                        <div className="bg-layout p-3 mb-3 border-radius-base">
                                          {renderServiceConfigurationHeadersEntities(entity, serviceConfigurationHeaders)}
                                        </div>
                                      </Col>

                                      {/* Remove Headers Entity Button */}
                                      <Col flex="none">
                                        <Button
                                          type="text"
                                          shape="circle"
                                          className="text-primary"
                                          icon={<DeleteIcon />}
                                          onClick={() => removeEntity(entity.name)}
                                        />
                                      </Col>
                                      {/* ./ Remove Headers Entity Button */}
                                    </Row>
                                  );
                                })}

                                <Form.ErrorList errors={responsesErrors} />

                                <Row>
                                  <Col>
                                    {/* Add Header Entity Button */}
                                    <Button
                                      type="dashed"
                                      size="large"
                                      block
                                      onClick={() => {
                                        addEntity();
                                      }}
                                      className="text-secondary mb-3"
                                      icon={<PlusIcon />}
                                      disabled={!canEdit}
                                    >
                                      {t('Add Header')}
                                    </Button>
                                    {/* Add Header Entity Button */}
                                  </Col>
                                </Row>
                              </Fragment>
                            );
                          }
                          }
                        </Form.List>
                        {/* ./ Headers */}
                      </Fragment>

                    ) : null
                  );
                }}
              </Form.Item>
              {/* ./ Service Configuration */}
            </Col>

          </Row>

          <Prompt
            when={isFieldsTouched}
            onConfirm={() => form.submit()}
            okText={t('Save Changes')}
            cancelText={t('Quit without saving')}
            title={t('Save Changes?')}
            message={t('Your unsaved changes will be lost. Save changes before closing?')}
          />
        </FormCard>
      );
    } else {
      return null;
    }
  };

  return renderContent();
};

export default PrivacySettingsForm;
